import * as THREE from 'three';
import { CAMERA, RENDER } from '../config';
import type { PlayerShip } from './PlayerShip';

export type CameraMode = 'chase' | 'cockpit';

/**
 * Chase / cockpit camera. Smoothing happens in ship-local space (rotation lag + offset),
 * never on world positions, so it stays stable at relativistic speeds.
 * Produces `worldPosition` (float64) which the game uses as the floating origin.
 */
export class ShipCamera {
  mode: CameraMode = 'chase';
  /** bridge eye position in ship space (set from the ship model) */
  readonly cockpitEye = new THREE.Vector3();
  readonly worldPosition = new THREE.Vector3();
  readonly quaternion = new THREE.Quaternion();
  private readonly offset = new THREE.Vector3();
  private fovKick = 0;
  /** 0..1 warp tunnel level (widens the FOV for a sense of speed) */
  warpLevel = 0;
  /** additive shake offset written by game feel (ship-local metres) */
  readonly shake = new THREE.Vector3();

  constructor(readonly camera: THREE.PerspectiveCamera) {}

  toggle(): void {
    this.mode = this.mode === 'chase' ? 'cockpit' : 'chase';
  }

  snap(ship: PlayerShip): void {
    this.quaternion.copy(ship.quaternion);
    this.offset.copy(this.targetOffset(ship));
    this.update(0, ship);
  }

  private targetOffset(ship: PlayerShip): THREE.Vector3 {
    const L = ship.spec.length;
    if (this.mode === 'cockpit') return this.cockpitEye.clone();
    return new THREE.Vector3(0, L * CAMERA.chaseHeight, L * CAMERA.chaseDistance);
  }

  update(dt: number, ship: PlayerShip): void {
    const cockpit = this.mode === 'cockpit';
    const rotK = cockpit ? 1 : 1 - Math.exp(-CAMERA.rotateRate * dt);
    this.quaternion.slerp(ship.quaternion, dt === 0 ? 1 : rotK);
    const target = this.targetOffset(ship);
    // Pull back a little at speed for a sense of acceleration.
    if (!cockpit) target.z += ship.spec.length * 0.2 * Math.min(1, ship.throttle) * (ship.boosting ? 1.8 : 1);
    const posK = dt === 0 ? 1 : 1 - Math.exp(-CAMERA.followRate * dt);
    this.offset.lerp(target, cockpit ? 1 : posK);

    const local = this.offset.clone().add(this.shake);
    this.worldPosition.copy(local.applyQuaternion(cockpit ? ship.quaternion : this.quaternion)).add(ship.position);

    const baseFov = cockpit ? RENDER.fovCockpit : RENDER.fovChase;
    const kickTarget = ship.boosting ? CAMERA.boostFovKick : 0;
    this.fovKick += (kickTarget - this.fovKick) * (1 - Math.exp(-4 * dt));
    const fov = baseFov + this.fovKick + this.warpLevel * 14;
    if (Math.abs(this.camera.fov - fov) > 0.01) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
    this.camera.position.set(0, 0, 0);
    this.camera.quaternion.copy(cockpit ? ship.quaternion : this.quaternion);
    this.camera.updateMatrixWorld(true);
  }
}
